/**
 * Wrap any button or link that needs the backend. With login on (REACT_APP_LOGIN_ENABLED=true) it renders the
 * child unchanged. With login off it keeps the child visible but greyed out and unclickable, with a
 * "Currently undergoing maintenance" tooltip that shows at once on hover, tap, or keyboard focus.
 * (Replaces the native title tooltip, which browsers show only after about a second.)
 */
import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { LOGIN_ENABLED } from "../../loginAvailability";
import { LOGIN_MAINTENANCE_TOOLTIP } from "../../../constants/platformCopy";
import "./DisabledWhileLoginOff.css";

type DisabledWhileLoginOffProps = {
  children: React.ReactElement;
  // Stretch to the parent's width, for full-width card buttons.
  block?: boolean;
};

let tooltipCounter = 0;

const blockActivation = (event: React.SyntheticEvent): void => {
  event.preventDefault();
  event.stopPropagation();
};

const DisabledWhileLoginOff: React.FC<DisabledWhileLoginOffProps> = ({ children, block = false }) => {
  const [tooltipId] = React.useState<string>(() => `login-off-tooltip-${++tooltipCounter}`);

  if (LOGIN_ENABLED) {
    return children;
  }

  return (
    <OverlayTrigger
      placement="bottom"
      trigger={["hover", "focus"]}
      overlay={<Tooltip id={tooltipId}>{LOGIN_MAINTENANCE_TOOLTIP}</Tooltip>}
    >
      <span
        className={block ? "login-off-disabled login-off-disabled--block" : "login-off-disabled"}
        role="button"
        aria-disabled="true"
        aria-describedby={tooltipId}
        tabIndex={0}
        onClickCapture={blockActivation}
        onKeyDownCapture={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            blockActivation(event);
          }
        }}
      >
        {children}
      </span>
    </OverlayTrigger>
  );
};

export default DisabledWhileLoginOff;
